import React, { useCallback } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import mailcheck from 'mailcheck';
import {
  Control,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import renderController from './lib/renderController';
import TextField, { TextFieldProperties } from './TextField';

const emailFieldPropertyTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  helperText: PropTypes.node,
  formatSuggestion: PropTypes.func,
  useStringOnClick: PropTypes.bool,
};

type EmailFieldProperties = Omit<
  InferProps<typeof emailFieldPropertyTypes>,
  'onChange' | 'formatSuggestion' | 'helperText'
> & Omit<TextFieldProperties, 'onChange' | 'helperText'> & {
  onChange?: (
    value: string | { target: { value: string } },
  ) => void;
  formatSuggestion?: (email: string) => React.ReactNode;
  helperText?: React.ReactNode;

};

const EmailField = React.forwardRef(
  (
    {
      value,
      onChange,
      helperText: originalHelperText,
      formatSuggestion,
      useStringOnClick,
      ...properties
    }: EmailFieldProperties,
    reference: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const emailValue = value ?? '';
    const formatSuggestionValue = formatSuggestion
      ?? ((email: string) => `Did you mean ${email}?`);
    const suggestedEmail = mailcheck.run({ email: emailValue })?.full ?? '';
    const onClick = useCallback(
      () => {
        if (useStringOnClick) {
          onChange?.(suggestedEmail);
          return;
        }
        onChange?.({
          target: { value: suggestedEmail },
        });
      },
      [onChange, suggestedEmail, useStringOnClick],
    );

    const emailSuggestion = (
      suggestedEmail && suggestedEmail !== emailValue
    ) && (
      <span
        onClick={onClick}
        onKeyDown={onClick}
        role="button"
        tabIndex={-1}
      >
        {formatSuggestionValue(suggestedEmail)}
      </span>
    );
    const helperText = emailSuggestion || originalHelperText;

    return (
      <TextField
        {...properties}
        type="text"
        onChange={onChange}
        value={emailValue}
        helperText={helperText}
        ref={reference}
      />
    );
  },
);

EmailField.propTypes = emailFieldPropertyTypes;

const emailFieldWithControllerPropertyTypes = {
  rules: PropTypes.shape({}),
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  defaultValue: PropTypes.string,
};

type EmailFieldWithControllerProperties<
  T extends FieldValues = FieldValues,
> = Omit<
  InferProps<typeof emailFieldWithControllerPropertyTypes>,
  'control' | 'rules'
> & Omit<
  EmailFieldProperties,
  'onChange' | 'value' | 'id'
> & {
  control: Control<T>;
  rules?: UseControllerProps['rules'];
  id: string;
};

export const EmailFieldWithController = <
  T extends FieldValues = FieldValues,
>({
    rules,
    name,
    control,
    defaultValue,
    id,
    ...properties
  }: EmailFieldWithControllerProperties<T>) => {
  const render = useCallback(
    ({
      field: {
        onChange, onBlur, value, ref,
      },
    }: {
      field: ControllerRenderProps<T, FieldPath<T>>;
    }) => (
      <EmailField
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        ref={ref}
        name={name}
        id={id}
        {...properties}
      />
    ),
    [name, id, properties],
  );
  return renderController<T>({
    control, name, rules, defaultValue: defaultValue ?? '', render,
  });
};

EmailFieldWithController
  .propTypes = emailFieldWithControllerPropertyTypes;

export default EmailField;
